/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Observable } from 'rxjs';

import {
  AbstractFormComponent,
  BuilderNavigationService,
  BuilderUserContext,
  CommonActions,
  FormActionPipelines,
  FormActions,
  FormFlowState,
} from '@gh/builder-shared';
import {
  BuilderUserProfileDto,
  Month,
  OfficeMeasurement,
  OfficeMeasurementDefault,
  OfficeStatus,
  OfficeType,
  UserType,
} from '@gh/builder-shared-data';
import {
  ActionFlow,
  AppRouter,
  DictionaryItem,
  DictionaryService,
  EnumDictionary,
  getDefaultEmail,
  getRouteInitData,
  TextDictionary,
} from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { DEFAULT_EMAIL_SUFFIX } from '@gh/config';
import { MessageBasket } from '@gh/core-messages';
import {
  AddressAutocompleteComponent,
  AddressComponents,
  AutocompleteLoadMoreEvent,
  DialogService,
  FormMode,
  getControlValue,
  NotificationService,
  setControlValue,
  validationError,
  WaitingService,
  yesAnswer,
  yesNoConfirmation,
} from '@gh/core-ui';
import { filter$, map$, switchMap$ } from '@gh/rx/operators';

import { assign, filter, isEmpty, isNil } from 'lodash';

import { MasterZone } from '../../resources/master-zone.model';
import { OfficeDetailsBusinessService } from '../../resources/office-details-business.service';
import { OfficeDetails } from '../../resources/office-details.model';
import { OfficeListItem } from '../../resources/office-list-item.model';
import { OfficeActiveRecord } from 'builder-setup/offices/resources/office-active-record-model';
import { OfficeDetailsFormState } from './office-details-form.service';
import { AbstractControl, FormArray, FormControl, FormGroup, ValidatorFn } from '@angular/forms';
import { TranslationService } from '@gh/core-mls';
import { UnpaidInvoicesDialogComponent, UnpaidInvoicesDialogData } from '@gh/builder-shared';

@Component({
  standalone: false,
  selector: 'gh-office-details',
  templateUrl: 'office-details.component.html',
  styleUrls: ['office-details.component.scss'],
  viewProviders: [OfficeDetailsFormState],
})
export class OfficeDetailsComponent extends AbstractFormComponent<OfficeDetails> implements OnInit, AfterViewInit {
  @ViewChild(AddressAutocompleteComponent) addressAutocomplete: AddressAutocompleteComponent | undefined;

  officeTypes: EnumDictionary<OfficeType>;
  officeStatuses: EnumDictionary<OfficeStatus>;
  masterZones: MasterZone[];
  states: DictionaryItem<string>[];
  templates: TextDictionary;
  measurements: EnumDictionary<OfficeMeasurement>;
  months: EnumDictionary<Month>;
  financialYears: number[];
  measurementDefaults: Hash<EnumDictionary<OfficeMeasurementDefault>>;
  emailSuffix = DEFAULT_EMAIL_SUFFIX;
  timeZones: TextDictionary;
  isModifyingOn: boolean = false;
  officeId: ID;
  activeCount: number = 0;
  inactiveCount: number = 0;
  showManualAddressFields = false;
  private _currentTab: string = 'common';
  private initialCustomMonthlyPrice: number | null = null;
  private originalCustomMonthlyPrice: number | null = null;
  private initialSystemName: string | null = null;

  constructor(private route: ActivatedRoute,
    private router: AppRouter,
    private businessService: OfficeDetailsBusinessService,
    public form: OfficeDetailsFormState,
    private pipelines: FormActionPipelines,
    private dictionaryService: DictionaryService,
    private commonActions: CommonActions,
    private formActions: FormActions,
    private dialogService: DialogService,
    private navigationService: BuilderNavigationService,
    private userContext: BuilderUserContext,
    private notifications: NotificationService,
    private translate: TranslationService,
    private waitings: WaitingService,
  ) {
    super();
  }

  get title(): string {
    return getControlValue(this.form.group, 'name');
  }

  get email(): string {
    return getDefaultEmail(getControlValue(this.form.group, 'email'));
  }

  get hasActivePlan(): boolean {
    const planName = getControlValue(this.form.group, 'planName');
    return !!planName;
  }

  get isStripeEnabled(): boolean {
    const user = this.userContext.data as BuilderUserProfileDto;
    return user?.isStripeEnabled ?? false;
  }

  get shouldShowSoftwareSubscription(): boolean {
    const officeType = getControlValue(this.form.group, 'type');
    const monthlyPrice = this.parsePriceValue(getControlValue(this.form.group, 'monthlyPrice'));

    if (officeType === OfficeType.Training || officeType === OfficeType.Template) {
      return false;
    }

    if ((monthlyPrice === null || monthlyPrice === 0) && this.originalCustomMonthlyPrice === null) {
      return false;
    }

    return true;
  }

  get showZeroFeeCaution(): boolean {
    const customMonthlyPriceControl = this.form.group.get('customMonthlyPrice');
    const value = this.parsePriceValue(customMonthlyPriceControl?.value);
    return this.form.createMode && value === 0;
  }

  get pageTitle(): string {
    return {
      [FormMode.CreateMode]: 'lbl_office_create',
      [FormMode.EditMode]: 'lbl_office_edit',
      [FormMode.ViewMode]: '',
    }[this.form.mode];
  }

  get currentTab(): string {
    return this._currentTab;
  }

  get isProcessEnabled(): boolean {
    const group = this.form.group;

    if (!group || !group.dirty) {
      return false;
    }

    const customMonthlyPriceControl = group.get('customMonthlyPrice');
    const isPriceControlDirty = !!customMonthlyPriceControl && customMonthlyPriceControl.dirty;
    const priceEqual = this.arePricesEqual(customMonthlyPriceControl?.value, this.initialCustomMonthlyPrice);

    if (isPriceControlDirty && priceEqual) {
      return this.hasDirtyControls(group, customMonthlyPriceControl ? [customMonthlyPriceControl] : []);
    }

    return true;
  }

  get completeFlow(): ActionFlow<FormFlowState<OfficeDetails>> {
    let invalidFormPath = '';
    if (this.form.group.controls['billing'].invalid) {
      invalidFormPath = 'billing';
    } else {
      if (this.form.group.invalid) {
        invalidFormPath = 'common';
      } else {
        invalidFormPath = '';
      }
    }
    if (!isEmpty(invalidFormPath)) {
      this.onTabChanged(invalidFormPath);
      if (this.form.group.controls[invalidFormPath]) {
        this.checkAndShowManualAddressFields();
        this.form.group.controls[invalidFormPath].markAllAsTouched();
      }
    }
    return this.pipelines.complete({
      noSuccessNotification: true,
      validate: this.commonActions.validate(
        (state: FormFlowState<OfficeDetails>) => this.validateBeforeProcess(state.value),
      ),
      execute: this.commonActions.execute((state: FormFlowState<OfficeDetails>) => {
        if (this.form.createMode) {
          return this.businessService.create(this.prepareSubmitPayload(state.form.submitValue));
        } else {
          return this.businessService.update(this.prepareSubmitPayload(state.form.submitValue));
        }
      }),
      finish: (state) => {
        const { value, form } = state;

        if (value.unpaidInvoices && value.unpaidInvoices.length > 0) {
          this.showUnpaidInvoicesWarning(value);
          return;
        }

        this.notifications.success('msg_saved_successfully');
        this.isModifyingOn = false;
        if (this.form.createMode) {
          this.router.switchTo(['/setup/offices', value.id]);
          this.resetProcessEnabledState(value.customMonthlyPrice);
        } else {
          this.form.refreshFinancialYearInformationSchema();
          this.businessService.get(this.officeId).pipe(this.waitings.operator()).subscribe(
            (latest) => {
              form.build(latest);
              this.originalCustomMonthlyPrice = this.parsePriceValue(latest.customMonthlyPrice);
              this.resetProcessEnabledState(latest.customMonthlyPrice);
            },
            this.notifications.processAll,
          );
        }
      },
    });
  }

  ngOnInit(): void {
    const { officeDetails, states, ...init } = getRouteInitData(this.route);
    this.states = states.seq().sort((a: { name: string; id: ID }, b: { name: string; id: ID }) => {
      if (a.name.toLowerCase() === 'overseas') {
        return 1;
      }
      if (a.name < b.name) {
        return -1;
      }
      if (a.name > b.name) {
        return 1;
      }
      return 0;
    });
    assign(this, init);
    if (officeDetails && officeDetails.statusValue === OfficeStatus.InDeletion) {
      this.router.navigateBack();
    }

    this.officeId = officeDetails.id;

    this.form.build(officeDetails);

    const planNameControl = this.form.group.get('planName');
    if (planNameControl) {
      setControlValue(this.form.group, 'planName', officeDetails.planName);
    }

    const monthlyPriceControl = this.form.group.get('monthlyPrice');
    if (monthlyPriceControl) {
      setControlValue(this.form.group, 'monthlyPrice', officeDetails.monthlyPrice);
    }

    const customMonthlyPriceControl = this.form.group.get('customMonthlyPrice');
    if (customMonthlyPriceControl) {
      setControlValue(this.form.group, 'customMonthlyPrice', officeDetails.customMonthlyPrice);
      this.initialCustomMonthlyPrice = this.parsePriceValue(officeDetails.customMonthlyPrice);
      this.originalCustomMonthlyPrice = this.parsePriceValue(officeDetails.customMonthlyPrice);
      this.resetProcessEnabledState(officeDetails.customMonthlyPrice);
    }
    this.form.group.get('billing.officeClosed')?.setValidators(
      this.dateRangeValidator(this.form.group.get('billing.contractSignedDate'), 'lbl_contract_signed_date'),
    );
    this.form.group.get('billing.renewalDate')?.setValidators(
      this.dateRangeValidator(this.form.group.get('billing.originalCommencement'), 'lbl_original_commencement'),
    );
    this.form.group.get('billing.currentAgreement')?.setValidators(
      this.dateRangeValidator(this.form.group.get('billing.originalCommencement'), 'lbl_original_commencement', true),
    );
    this.form.group.get('billing.onSellDate')?.setValidators(
      this.dateRangeValidator(this.form.group.get('billing.contractSignedDate'), 'lbl_contract_signed_date'),
    );
    this.form.group.get('type')?.valueChanges.subscribe((type) => {
      if (type === OfficeType.Training) {
        this.form.group.get('publishToWeb')?.setValue(false);
        this.form.group.get('publishToWeb')?.disable();
      } else {
        this.form.group.get('publishToWeb')?.enable();
      }
    });

    if (this.form.createMode) {
      const userMasterZone = this.userContext.masterZone;
      if (userMasterZone) {
        const currentMZ = this.masterZones.find((mz) => mz.id === userMasterZone.id);
        if (currentMZ) {
          setControlValue(this.form.group, 'sourceMasterZone', currentMZ);
        }
      }
    } else if (officeDetails.sourceOffice) {
      const sourceOffice = officeDetails.sourceOffice;
      const matchingMZ = this.masterZones.find((mz) => mz.id === sourceOffice.masterZoneId);
      if (matchingMZ) {
        setControlValue(this.form.group, 'sourceMasterZone', matchingMZ);
      }
    }

    this.form.group.addControl('includeInactive', new FormControl(false));
    if (officeDetails.type === OfficeType.Training) {
      this.form.group.get('publishToWeb')?.disable();
    }

    if (this.isSourceOfficeValid()) {
      const sourceOffice = getControlValue(this.form.group, 'sourceOffice');
      this.onSourceOfficeChange(sourceOffice);
    }
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.onAddressChange();

      const hasAddressData = !!(this.form.group.get('street1')?.value ||
        this.form.group.get('street2')?.value ||
        this.form.group.get('city')?.value ||
        this.form.group.get('state')?.value ||
        this.form.group.get('postCode')?.value);
      this.showManualAddressFields = hasAddressData;
    });
  }

  minDate(type: string): Date {
    return this.form.get(type)?.value;
  }

  minRenewalDate(): Date {
    const currentAgreement = this.form.get('billing.currentAgreement')?.value;
    const originalCommencement = this.form.get('billing.originalCommencement')?.value;
    if (currentAgreement && originalCommencement) {
      return currentAgreement.getTime() > originalCommencement.getTime() ? currentAgreement : originalCommencement;
    } else {
      if (!currentAgreement && originalCommencement) {
        return originalCommencement;
      } else {
        if (!originalCommencement && currentAgreement) {
          return currentAgreement;
        } else {
          return new Date(1970);
        }
      }
    }
  }

  onSystemNameChanged(): void {
    this.form.updateEmail();
  }

  onApplyLicense(): void {
    this.form.updateLicenseState();
  }

  onMasterZoneChange(): void {
    this.form.updateTimeZone();
  }

  onMeasurementChange(): void {
    this.form.updateMeasurementState();
  }

  onTabChanged(name: string): void {
    if (this._currentTab !== name) {
      this._currentTab = name;
    }
  }

  onAssignmentFeeUpdate(): void {
    this.form.onRecalculateAssignmentFee();
  }

  onRenewalDateUpdate(): void {
    this.form.onRecalculateRenewalDate();
  }

  goToAel(entityId?: ID, entityType?: string, label?: string): void {
    this.navigationService.goToAel({
      entityId: entityId ? entityId : undefined,
      entityType: entityType ? entityType : 'OFFICE',
      label: label ? label : 'lbl_menu_offices',
    });
  }

  loadOffices($event: AutocompleteLoadMoreEvent): void {
    const masterZone = getControlValue(this.form.group, 'sourceMasterZone');
    const offices$: Observable<any[]> = this.businessService.findOfficesByName($event.query, masterZone?.id).pipe(
      map$((offices: any[]) => {
        if ($event.query.from === 0 && !offices.some(o => o.name === 'DO NOT IMPORT DATA')) {
          const doNotImportOption = { name: 'DO NOT IMPORT DATA' };
          offices.unshift(doNotImportOption);
        }
        return offices;
      }),
    );
    $event.load(offices$);
  }

  onSourceMasterZoneChange(): void {
    setControlValue(this.form.group, 'sourceOffice', null);
    this.activeCount = 0;
    this.inactiveCount = 0;
    this.form.resetTemplates();
  }

  onSourceOfficeChange(office: OfficeListItem): void {
    if (isNil(office)) {
      this.activeCount = 0;
      this.inactiveCount = 0;
      this.form.resetTemplates();
      return;
    }
    if (office.name === "DO NOT IMPORT DATA") return;
    const officeActiveRecord = this.mapToOfficeActiveRecord(office);
    this.businessService.findActiveRecordsByOfficeName(officeActiveRecord).subscribe((value) => {
      this.activeCount = value.activeRecordsCount;  // Update active count
      this.inactiveCount = value.inactiveRecordsCount;
    });
  }

  mapToOfficeActiveRecord(office: OfficeListItem): OfficeActiveRecord {
    return {
      officeId: office.officeId,
      statusValue: office.statusValue,
      activeRecordsCount: 0,
      inactiveRecordsCount: 0,
    };
  }

  isSourceOfficeValid(): boolean {
    const sourceOfficeValue = this.form.group.get('sourceOffice')?.value;
    return sourceOfficeValue && sourceOfficeValue.name !== 'DO NOT IMPORT DATA';
  }

  onInactiveRecordsToggle(checked: boolean): void {
    this.form.group.get('includeInactive')?.setValue(checked);
  }

  isTemplateChecked(name: string): boolean {
    return this.form.isTemplateChecked(name);
  }

  isActivateVisible(): boolean {
    const status = this.officeStatuses.get(this.form.get('status').value);
    return this.form.editMode &&
      this.userContext.userType === UserType.Corporate &&
      status === OfficeStatus.Blocked;
  }

  isBlockVisible(): boolean {
    const status = this.officeStatuses.get(this.form.get('status').value);
    return this.form.editMode &&
      this.userContext.userType === UserType.Corporate &&
      status === OfficeStatus.Active;
  }

  isTemplatePartlyChecked(name: string): boolean {
    return this.form.isTemplatePartlyChecked(name);
  }

  onTemplateChecked(name: string, checked: boolean): void {
    this.form.markChildTemplates(name, checked);
  }

  onModifyFinancialInformation(): void {
    this.dialogService.confirmation(yesNoConfirmation('msg_accounting_data_edit')).pipe(
      filter$(yesAnswer))
      .subscribe(() => {
        this.form.enableFinancialInformationControls();
        this.isModifyingOn = true;
      });
  }

  onBlockOffice(): void {
    if (this.form.dirty) {
      this.notifications.error('msg_save_first');
    } else {
      this.dialogService.confirmation(yesNoConfirmation('msg_office_block_confirmation')).pipe(
        filter$(yesAnswer),
        switchMap$(() => this.businessService.blockOffice(this.officeId).pipe(this.waitings.operator())))
        .subscribe(
          (value) => {
            this.form.build(value);
            this.resetProcessEnabledState(value.customMonthlyPrice);
          },
          this.notifications.processAll);
    }
  }

  onActivateOffice(): void {
    if (this.form.dirty) {
      this.notifications.error('msg_save_first');
    } else {
      this.businessService.activateOffice(this.officeId).pipe(
        this.waitings.operator())
        .subscribe(
          (value) => {
            this.form.build(value);
            this.resetProcessEnabledState(value.customMonthlyPrice);
          },
          this.notifications.processAll,
        );
    }
  }

  dateRangeValidator(minValueControl: any, label: string, orEqual?: boolean): ValidatorFn {
    const fieldName = this.translate.get(label);
    return (control: AbstractControl): { [key: string]: any } | null => {
      const value = control?.value;
      const minValue = minValueControl?.value;
      const errorMessage = orEqual ? 'msg_incorrect_date_range_equal' : 'msg_incorrect_date_range';
      const error = {
        incorrectDate: validationError(errorMessage, {
          name: fieldName,
        }),
      };
      if ((
        orEqual && minValue && value && minValue > value
      ) || (
          !orEqual && minValue && value && minValue >= value
        )) {
        setTimeout(() => {
          control.setErrors(error);
        }, 0);
      }
      return null;
    };
  }

  private validateBeforeProcess(value: OfficeDetails): Maybe<MessageBasket> {
    if (this.form.createMode
      && value.sourceOffice && value.sourceOffice.name !== 'DO NOT IMPORT DATA' && isEmpty(filter(value.templateFlatHierarchy, (template) => template.checked))) {
      return MessageBasket.error('msg_office_no_template_selected');
    }

    const customMonthlyPriceControl = this.form.group.get('customMonthlyPrice');
    if (customMonthlyPriceControl) {
      const currentCustomPrice = this.parsePriceValue(value.customMonthlyPrice);
      const monthlyPrice = this.parsePriceValue(getControlValue(this.form.group, 'monthlyPrice'));

      if (currentCustomPrice !== null && monthlyPrice !== null && this.arePricesEqual(currentCustomPrice, monthlyPrice)) {
        if (this.form.editMode) {
          if (this.originalCustomMonthlyPrice === null) {
            customMonthlyPriceControl.setErrors({
              customFeeEqualsDefault: validationError('msg_custom_fee_cannot_equal_default')
            });
            customMonthlyPriceControl.markAsTouched();
            return MessageBasket.error('msg_custom_fee_cannot_equal_default');
          }
        } else {
          customMonthlyPriceControl.setErrors({
            customFeeEqualsDefault: validationError('msg_custom_fee_cannot_equal_default')
          });
          customMonthlyPriceControl.markAsTouched();
          return MessageBasket.error('msg_custom_fee_cannot_equal_default');
        }
      }
    }

    if (this.form.editMode) {
      if (customMonthlyPriceControl && customMonthlyPriceControl.dirty) {
        const currentCustomPrice = this.parsePriceValue(value.customMonthlyPrice);
        const initialPrice = this.initialCustomMonthlyPrice;

        if (initialPrice !== null && currentCustomPrice === null) {
          customMonthlyPriceControl.setErrors({ required: true });
          customMonthlyPriceControl.markAsTouched();
          return MessageBasket.error('msg_custom_fee_cannot_be_empty');
        }
      }
    }
  }

  private prepareSubmitPayload(submitValue: any): any {
    if (submitValue.sourceOffice && submitValue.sourceOffice.name === 'DO NOT IMPORT DATA') {
      submitValue.sourceOffice = null;
    }
    return submitValue;
  }

  onProcess(): void {
    const systemNameControl = this.form.group.get('systemName');
    const systemNameChanged = this.form.editMode && systemNameControl?.dirty && systemNameControl.value !== this.initialSystemName;

    if (systemNameChanged) {
      this.dialogService.confirmation(yesNoConfirmation('msg_office_system_name_change_confirmation')).pipe(
        filter$(yesAnswer))
        .subscribe(() => {
          this.initialSystemName = systemNameControl!.value;
          this.onProcess();
        });
      return;
    }

    const customMonthlyPriceControl = this.form.group.get('customMonthlyPrice');
    const currentValue = customMonthlyPriceControl?.value;

    // Check if customMonthlyPrice has changed
    const customMonthlyPriceChanged = this.form.editMode
      && !!customMonthlyPriceControl
      && customMonthlyPriceControl.dirty
      && !this.arePricesEqual(currentValue, this.initialCustomMonthlyPrice);

    if (customMonthlyPriceChanged) {
      const currentParsed = this.parsePriceValue(currentValue);
      const initialParsed = this.initialCustomMonthlyPrice;

      if (initialParsed !== null && currentParsed === null) {
        this.processFlow.run({
          trigger: 'process',
          form: this.form,
          value: this.form.submitValue,
        });
      } else {
        this.showCustomMonthlyPriceConfirmationDialog(currentValue);
      }
    } else {
      // No change to customMonthlyPrice, proceed with normal process flow
      this.processFlow.run({
        trigger: 'process',
        form: this.form,
        value: this.form.submitValue,
      });
    }
  }

  private hasDirtyControls(control: AbstractControl, excludedControls: AbstractControl[] = []): boolean {
    if (excludedControls.includes(control)) {
      return false;
    }

    if (control instanceof FormGroup) {
      return Object.keys(control.controls)
        .some((key) => this.hasDirtyControls(control.controls[key], excludedControls));
    }

    if (control instanceof FormArray) {
      return control.controls.some((childControl: AbstractControl) => this.hasDirtyControls(childControl, excludedControls));
    }

    return control.dirty;
  }

  private parsePriceValue(value: number | string | null | undefined): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    const parsed = Number(value);
    return Number.isNaN(parsed) ? null : parsed;
  }

  private arePricesEqual(first: number | string | null | undefined, second: number | string | null | undefined): boolean {
    const firstParsed = this.parsePriceValue(first);
    const secondParsed = this.parsePriceValue(second);

    if (firstParsed === null && secondParsed === null) {
      return true;
    }

    if (firstParsed === null || secondParsed === null) {
      return false;
    }

    return Math.round(firstParsed * 100) === Math.round(secondParsed * 100);
  }

  private resetProcessEnabledState(customMonthlyPrice: number | string | null | undefined): void {
    this.initialCustomMonthlyPrice = this.parsePriceValue(customMonthlyPrice);
    this.initialSystemName = getControlValue(this.form.group, 'systemName');
  }

  private showCustomMonthlyPriceConfirmationDialog(currentFee: number | string | null): void {
    let message = '';

    const normalizedCurrentFee = this.parsePriceValue(currentFee);

    if (normalizedCurrentFee === null || normalizedCurrentFee === 0) {
      message = 'Setting the fee to zero will cancel the current office subscription. Do you want to continue?';
    } else {
      message = `Changing the fee will schedule the cancellation of the current office subscription at the end of its billing period and create a new subscription with the updated fee (${normalizedCurrentFee.toFixed(2)}). Do you want to continue`;
    }

    this.dialogService
      .confirmation(yesNoConfirmation(message))
      .pipe(filter$(yesAnswer))
      .subscribe(() => {
        // User confirmed - update previous value and process the form
        this.initialCustomMonthlyPrice = normalizedCurrentFee;
        this.processFlow.run({
          trigger: 'process',
          form: this.form,
          value: this.form.submitValue,
        });
      });
  }

  onAddressChange() {
    const street1 = this.form.group.get('street1')?.value || '';
    const street2 = this.form.group.get('street2')?.value || '';
    const city = this.form.group.get('city')?.value || '';
    const state = this.form.group.get('state')?.value || '';
    const postCode = this.form.group.get('postCode')?.value || '';

    const addressParts: string[] = [];

    const streetCombined = [street2, street1].filter(Boolean).join(' ');
    if (streetCombined) addressParts.push(streetCombined);

    if (city) addressParts.push(city);
    if (state) addressParts.push(state);
    if (postCode) addressParts.push(postCode);

    const newAddress = addressParts.join(', ');

    if (this.addressAutocomplete) {
      this.addressAutocomplete.inputValue = newAddress;
    }
  }

  onAddressSelected(components: AddressComponents): void {
    const streetParts: string[] = [];
    if (components.streetNo) streetParts.push(components.streetNo);
    if (components.street) streetParts.push(components.street);
    const street1Value = streetParts.join(' ').trim();

    this.form.group.patchValue({
      street1: street1Value || '',
      city: components.city || '',
      state: components.state || '',
      postCode: components.postalCode || ''
    });

    ['street1', 'city', 'state', 'postCode'].forEach(field => {
      const control = this.form.group.get(field);
      if (control) control.markAsDirty();
    });
  }

  private checkAndShowManualAddressFields(): void {
    if (this.showManualAddressFields) return;

    const addressFields = ['street1', 'city', 'state', 'postCode'];
    const hasAddressErrors = addressFields.some(field => {
      const control = this.form.group.get(field);
      return control && control.invalid;
    });

    if (hasAddressErrors) {
      this.showManualAddressFields = true;
    }
  }

  private showUnpaidInvoicesWarning(office: OfficeDetails): void {
    const input: UnpaidInvoicesDialogData = {
      unpaidInvoices: office.unpaidInvoices!,
      productName: office.name,
      customMessage: 'This office has unpaid invoices. Please review and settle them before updating the custom monthly price.',
      showProceedButton: true
    };
    const dialogRef = this.dialogService.openWithInput(UnpaidInvoicesDialogComponent, input, { disableClose: true });

    dialogRef.afterClosed().subscribe((acknowledged) => {
      if (acknowledged) {
        const payload = this.prepareSubmitPayload(this.form.submitValue);
        this.businessService.update(payload, true).pipe(this.waitings.operator()).subscribe((result) => {
          this.form.refreshFinancialYearInformationSchema();
          this.form.build(result);
          this.resetProcessEnabledState(result.customMonthlyPrice);
          this.isModifyingOn = false;
          this.notifications.success('msg_saved_successfully');
        }, this.notifications.processAll);
      } else {
        // User cancelled, fetch the latest data from the backend to reset the form
        this.businessService.get(this.officeId).pipe(this.waitings.operator()).subscribe((latestOffice) => {
          this.form.build(latestOffice);
          this.resetProcessEnabledState(latestOffice.customMonthlyPrice);
          this.isModifyingOn = false;
        }, this.notifications.processAll);
      }
    });
  }
}
